/**
 * Type Reference Documentation
 *
 * Data types and interfaces available in QSL strategies.
 */
import type { DocPage } from '../types';

export const typesDocs: DocPage[] = [
  {
    id: 'types/contexts',
    title: 'Context Objects',
    category: 'types',
    tags: ['context', 'DefineContext', 'InitContext', 'OnBarContext', 'ctx', 'types'],
    version: '2.0',
    related: ['api/lifecycle/define', 'api/lifecycle/init', 'api/lifecycle/onBar'],
    order: 1,
    description: 'The three context objects passed to lifecycle functions',
    content: `# Context Objects

Each lifecycle function receives a different context object. The context provides access to the APIs available at that stage.

## DefineContext

Passed to \`define(ctx)\`. Only has the \`param()\` method.

\`\`\`typescript
interface DefineContext {
  param(name: string, options: ParamDefinition): void;
}
\`\`\`

## InitContext

Passed to \`init(ctx)\`. Provides parameter values, indicator declaration, and state.

\`\`\`typescript
interface InitContext {
  readonly p: Record<string, number | string | boolean>;
  indicator(name: string, type: IndicatorType, options: IndicatorOptions): void;
  state: Record<string, unknown>;
}
\`\`\`

| Property | Description |
|----------|-------------|
| \`ctx.p\` | Read-only parameter values (set before init runs) |
| \`ctx.indicator()\` | Declare a host-computed indicator |
| \`ctx.state\` | Mutable object for tracking state between bars |

## OnBarContext

Passed to \`onBar(ctx, i)\`. Full access to indicators, price data, orders, and state.

\`\`\`typescript
interface OnBarContext {
  readonly p: Record<string, number | string | boolean>;
  readonly ind: Record<string, number[] | MACDResult | BBandsResult | DonchianResult | StochResult>;
  readonly series: Series;
  readonly order: OrderAPI;
  readonly barIndex: number;
  position(symbol: string): Position;
  state: Record<string, unknown>;
}
\`\`\`

| Property | Description |
|----------|-------------|
| \`ctx.p\` | Parameter values |
| \`ctx.ind\` | Computed indicator data arrays |
| \`ctx.series\` | OHLCV price data |
| \`ctx.order\` | Order execution API |
| \`ctx.barIndex\` | Current bar index (same as \`i\`) |
| \`ctx.position()\` | Current position for a symbol |
| \`ctx.state\` | Mutable state object |
`,
  },
  {
    id: 'types/indicators',
    title: 'Indicator Types',
    category: 'types',
    subcategory: 'indicators',
    tags: ['indicator', 'SMA', 'EMA', 'WMA', 'DEMA', 'TEMA', 'RSI', 'MACD', 'BBANDS', 'ATR', 'DONCHIAN', 'STOCH', 'STOCHRSI', 'ADX', 'CCI', 'WMA', 'VWAP', 'OBV', 'MFI', 'PPO', 'WILLIAMSR', 'ROC', 'TRIX', 'PSAR', 'KELTNER', 'ICHIMOKU', 'ADL', 'AO', 'types'],
    version: '2.0',
    related: ['api/lifecycle/init', 'types/indicator-results'],
    order: 2,
    description: 'All available indicator types and their configuration options',
    content: `# Indicator Types

Indicators are declared in \`init()\` and computed automatically by the engine before \`onBar()\` runs. They are accessed via \`ctx.ind[name]\`.

## Available Indicators

### Moving Averages

| Type | Name | Result | Key Options |
|------|------|--------|-------------|
| \`SMA\` | Simple Moving Average | \`number[]\` | \`period\`, \`source\` |
| \`EMA\` | Exponential Moving Average | \`number[]\` | \`period\`, \`source\` |
| \`WMA\` | Weighted Moving Average | \`number[]\` | \`period\`, \`source\` |
| \`DEMA\` | Double Exponential Moving Average | \`number[]\` | \`period\`, \`source\` |
| \`TEMA\` | Triple Exponential Moving Average | \`number[]\` | \`period\`, \`source\` |

### Trend

| Type | Name | Result | Key Options |
|------|------|--------|-------------|
| \`MACD\` | Moving Average Convergence Divergence | \`MACDResult\` | \`fastPeriod\`, \`slowPeriod\`, \`signalPeriod\` |
| \`ADX\` | Average Directional Index | \`ADXResult\` | \`period\` |
| \`PSAR\` | Parabolic SAR | \`number[]\` | \`step\`, \`max\` |
| \`ICHIMOKU\` | Ichimoku Cloud | \`IchimokuResult\` | \`tenkanPeriod\`, \`kijunPeriod\`, \`senkouBPeriod\` |
| \`TRIX\` | Triple EMA Oscillator | \`number[]\` | \`period\`, \`source\` |

### Momentum / Oscillators

| Type | Name | Result | Key Options |
|------|------|--------|-------------|
| \`RSI\` | Relative Strength Index | \`number[]\` | \`period\`, \`source\` |
| \`STOCH\` | Stochastic Oscillator | \`StochResult\` | \`kPeriod\`, \`dPeriod\` |
| \`STOCHRSI\` | Stochastic RSI | \`StochRSIResult\` | \`rsiPeriod\`, \`stochPeriod\`, \`kPeriod\`, \`dPeriod\` |
| \`CCI\` | Commodity Channel Index | \`number[]\` | \`period\` |
| \`WILLIAMSR\` | Williams %R | \`number[]\` | \`period\` |
| \`ROC\` | Rate of Change | \`number[]\` | \`period\`, \`source\` |
| \`AO\` | Awesome Oscillator | \`number[]\` | \`fastPeriod\`, \`slowPeriod\` |
| \`PPO\` | Percentage Price Oscillator | \`number[]\` | \`shortGamma\`, \`longGamma\`, \`accuracy\` |

### Volatility

| Type | Name | Result | Key Options |
|------|------|--------|-------------|
| \`BBANDS\` | Bollinger Bands | \`BBandsResult\` | \`period\`, \`stdDev\`, \`source\` |
| \`ATR\` | Average True Range | \`number[]\` | \`period\` |
| \`DONCHIAN\` | Donchian Channel | \`DonchianResult\` | \`period\` |
| \`KELTNER\` | Keltner Channels | \`KeltnerResult\` | \`period\`, \`multiplier\` |

### Volume

| Type | Name | Result | Key Options |
|------|------|--------|-------------|
| \`OBV\` | On-Balance Volume | \`number[]\` | — |
| \`MFI\` | Money Flow Index | \`number[]\` | \`period\` |
| \`VWAP\` | Volume Weighted Average Price | \`number[]\` | — |
| \`ADL\` | Accumulation/Distribution Line | \`number[]\` | — |

## Common Options

\`\`\`typescript
interface IndicatorOptions {
  period?: number;
  source?: 'open' | 'high' | 'low' | 'close' | 'volume' | 'hl2' | 'hlc3' | 'ohlc4';
  fastPeriod?: number;
  slowPeriod?: number;
  signalPeriod?: number;
  stdDev?: number;
  kPeriod?: number;
  dPeriod?: number;
  multiplier?: number;
  step?: number;
  max?: number;
  tenkanPeriod?: number;
  kijunPeriod?: number;
  senkouBPeriod?: number;
  rsiPeriod?: number;
  stochPeriod?: number;
}
\`\`\`

| Option | Used By | Description |
|--------|---------|-------------|
| \`period\` | Most indicators | Look-back window |
| \`source\` | SMA, EMA, WMA, DEMA, TEMA, RSI, ROC, TRIX, BBANDS | Price source (\`'close'\` default) |
| \`fastPeriod\` | MACD, PPO, AO | Fast component period |
| \`slowPeriod\` | MACD, PPO, AO | Slow component period |
| \`signalPeriod\` | MACD, PPO | Signal line period |
| \`stdDev\` | BBANDS | Standard deviation multiplier |
| \`kPeriod\` | STOCH, STOCHRSI | %K period |
| \`dPeriod\` | STOCH, STOCHRSI | %D period |
| \`multiplier\` | KELTNER | ATR multiplier for band width |
| \`step\` | PSAR | Acceleration factor increment |
| \`max\` | PSAR | Maximum acceleration factor |
| \`tenkanPeriod\` | ICHIMOKU | Conversion line period |
| \`kijunPeriod\` | ICHIMOKU | Base line period |
| \`senkouBPeriod\` | ICHIMOKU | Senkou Span B period |
| \`rsiPeriod\` | STOCHRSI | RSI calculation period |
| \`stochPeriod\` | STOCHRSI | Stochastic lookback period |

## Declaration Examples

\`\`\`javascript
function init(ctx) {
  // Moving averages — result is number[]
  ctx.indicator('sma20', 'SMA', { period: 20, source: 'close' });
  ctx.indicator('dema', 'DEMA', { period: 21, source: 'close' });

  // Oscillators — result is number[]
  ctx.indicator('rsi14', 'RSI', { period: 14 });
  ctx.indicator('cci', 'CCI', { period: 20 });
  ctx.indicator('willr', 'WILLIAMSR', { period: 14 });

  // Volatility — result is number[]
  ctx.indicator('atr14', 'ATR', { period: 14 });

  // Trend — compound result
  ctx.indicator('macd', 'MACD', {
    fastPeriod: 12, slowPeriod: 26, signalPeriod: 9,
  });
  ctx.indicator('adx', 'ADX', { period: 14 });
  ctx.indicator('psar', 'PSAR', { step: 0.02, max: 0.2 });
  ctx.indicator('ichimoku', 'ICHIMOKU', {
    tenkanPeriod: 9, kijunPeriod: 26, senkouBPeriod: 52,
  });

  // Bands — compound result
  ctx.indicator('bb', 'BBANDS', { period: 20, stdDev: 2, source: 'close' });
  ctx.indicator('dc', 'DONCHIAN', { period: 20 });
  ctx.indicator('kc', 'KELTNER', { period: 20, multiplier: 2 });

  // Stochastic family — compound result
  ctx.indicator('stoch', 'STOCH', { kPeriod: 14, dPeriod: 3 });
  ctx.indicator('stochRsi', 'STOCHRSI', {
    rsiPeriod: 14, stochPeriod: 14, kPeriod: 3, dPeriod: 3,
  });

  // Volume — result is number[]
  ctx.indicator('obv', 'OBV', {});
  ctx.indicator('mfi', 'MFI', { period: 14 });
  ctx.indicator('adl', 'ADL', {});
}
\`\`\`

## Using Parameterised Periods

The most common pattern — use \`ctx.p\` so the optimizer can tune indicator periods:

\`\`\`javascript
function define(ctx) {
  ctx.param('period', { type: 'int', default: 14, min: 5, max: 50, optimize: true });
}

function init(ctx) {
  ctx.indicator('ema', 'EMA', { period: ctx.p.period, source: 'close' });
}
\`\`\`
`,
  },
  {
    id: 'types/indicator-results',
    title: 'Indicator Results',
    category: 'types',
    subcategory: 'indicators',
    tags: ['MACD', 'BBANDS', 'DONCHIAN', 'STOCH', 'STOCHRSI', 'ADX', 'KELTNER', 'ICHIMOKU', 'result', 'histogram', 'signal', 'upper', 'lower', 'middle'],
    version: '2.0',
    related: ['types/indicators', 'api/lifecycle/onBar'],
    order: 3,
    description: 'Return shapes for compound indicators (MACD, Bollinger, Donchian, Stochastic, ADX, Keltner, Ichimoku)',
    content: `# Indicator Results

Most indicators return a plain \`number[]\`. Some return an object with multiple arrays.

## Simple Indicators (number[])

SMA, EMA, WMA, DEMA, TEMA, RSI, ATR, CCI, OBV, MFI, VWAP, PPO, WILLIAMSR, ROC, TRIX, PSAR, AO, ADL all return a single \`number[]\` array:

\`\`\`javascript
const emaValue = ctx.ind.ema[i];  // number
\`\`\`

Before the indicator has enough data to compute, the value is \`NaN\`. Always check:

\`\`\`javascript
if (q.isNaN(ctx.ind.ema[i])) return;
\`\`\`

## Multi-Output Access Pattern

Multi-output indicators are stored as flat underscore-separated keys in \`ctx.ind\`:

\`\`\`javascript
// Declared as: ctx.indicator('macd', 'MACD', { ... })
// Accessed as: ctx.ind.macd_line, ctx.ind.macd_signal, ctx.ind.macd_histogram
\`\`\`

## MACDResult

\`\`\`typescript
interface MACDResult {
  line: number[];      // MACD line
  signal: number[];    // Signal line
  histogram: number[]; // MACD - Signal
}
\`\`\`

\`\`\`javascript
const line = ctx.ind.macd_line[i];
const signal = ctx.ind.macd_signal[i];
if (q.crossOver(ctx.ind.macd_line, ctx.ind.macd_signal, i)) {
  ctx.order.market('ASSET', 1, { signal: 'macd_bullish' });
}
\`\`\`

## BBandsResult

\`\`\`typescript
interface BBandsResult {
  upper: number[];   // Upper band
  middle: number[];  // Middle band (SMA)
  lower: number[];   // Lower band
}
\`\`\`

\`\`\`javascript
const upper = ctx.ind.bb_upper[i];
const lower = ctx.ind.bb_lower[i];
if (ctx.series.close[i] < lower) {
  ctx.order.market('ASSET', 1, { signal: 'bb_oversold' });
}
\`\`\`

## DonchianResult

\`\`\`typescript
interface DonchianResult {
  upper: number[];   // Highest high over period
  middle: number[];  // Average of upper and lower
  lower: number[];   // Lowest low over period
}
\`\`\`

\`\`\`javascript
const upper = ctx.ind.dc_upper[i];
const lower = ctx.ind.dc_lower[i];
if (ctx.series.close[i] > upper) {
  ctx.order.market('ASSET', 1, { signal: 'breakout' });
}
\`\`\`

## StochResult

\`\`\`typescript
interface StochResult {
  k: number[];  // %K (fast stochastic)
  d: number[];  // %D (slow stochastic)
}
\`\`\`

\`\`\`javascript
const k = ctx.ind.stoch_k[i];
const d = ctx.ind.stoch_d[i];
if (k < 20 && q.crossOver(ctx.ind.stoch_k, ctx.ind.stoch_d, i)) {
  ctx.order.market('ASSET', 1, { signal: 'stoch_oversold' });
}
\`\`\`

## StochRSIResult

\`\`\`typescript
interface StochRSIResult {
  k: number[];  // %K line
  d: number[];  // %D line
}
\`\`\`

\`\`\`javascript
const k = ctx.ind.stochRsi_k[i];
const d = ctx.ind.stochRsi_d[i];
\`\`\`

## ADXResult

\`\`\`typescript
interface ADXResult {
  adx: number[];  // ADX (trend strength 0-100)
  pdi: number[];  // +DI (positive directional indicator)
  mdi: number[];  // -DI (minus directional indicator)
}
\`\`\`

\`\`\`javascript
const adxValue = ctx.ind.adx[i];     // Primary (no suffix)
const plusDI = ctx.ind.adx_pdi[i];
const minusDI = ctx.ind.adx_mdi[i];
\`\`\`

## KeltnerResult

\`\`\`typescript
interface KeltnerResult {
  upper: number[];   // Upper channel
  middle: number[];  // Middle (EMA)
  lower: number[];   // Lower channel
}
\`\`\`

\`\`\`javascript
const upper = ctx.ind.kc_upper[i];
const middle = ctx.ind.kc_middle[i];
const lower = ctx.ind.kc_lower[i];
\`\`\`

## IchimokuResult

\`\`\`typescript
interface IchimokuResult {
  tenkan: number[];   // Tenkan-sen (conversion line)
  kijun: number[];    // Kijun-sen (base line)
  senkouA: number[];  // Senkou Span A (leading span A)
  senkouB: number[];  // Senkou Span B (leading span B)
  chikou: number[];   // Chikou Span (lagging line)
}
\`\`\`

\`\`\`javascript
const tenkan = ctx.ind.ichimoku_tenkan[i];
const kijun = ctx.ind.ichimoku_kijun[i];
const cloudTop = Math.max(ctx.ind.ichimoku_senkouA[i], ctx.ind.ichimoku_senkouB[i]);
\`\`\`
`,
  },
  {
    id: 'types/series',
    title: 'Series (OHLCV)',
    category: 'types',
    tags: ['series', 'OHLCV', 'open', 'high', 'low', 'close', 'volume', 'timestamp', 'price', 'candle'],
    version: '2.0',
    related: ['api/lifecycle/onBar', 'types/contexts'],
    order: 4,
    description: 'Price and volume series data available in onBar',
    content: `# Series (OHLCV)

The \`ctx.series\` object provides all candle data for the backtest period. Each property is a \`number[]\` array indexed by bar number.

## Structure

\`\`\`typescript
interface Series {
  open: number[];       // Opening price
  high: number[];       // Highest price
  low: number[];        // Lowest price
  close: number[];      // Closing price
  volume: number[];     // Trade volume
  timestamp: number[];  // Unix timestamp (milliseconds)
  length: number;       // Total number of bars
}
\`\`\`

## Usage in onBar

\`\`\`javascript
function onBar(ctx, i) {
  const open   = ctx.series.open[i];
  const high   = ctx.series.high[i];
  const low    = ctx.series.low[i];
  const close  = ctx.series.close[i];
  const volume = ctx.series.volume[i];

  // Previous bar
  const prevClose = ctx.series.close[i - 1];

  // Price range
  const range = high - low;

  // Candle body
  const body = Math.abs(close - open);
  const isBullish = close > open;
}
\`\`\`

## Composite Prices

Some indicators accept a \`source\` option. The engine computes these automatically:

| Source | Formula |
|--------|---------|
| \`'open'\` | Open price |
| \`'high'\` | High price |
| \`'low'\` | Low price |
| \`'close'\` | Close price (default) |
| \`'hl2'\` | (High + Low) / 2 |
| \`'hlc3'\` | (High + Low + Close) / 3 |
| \`'ohlc4'\` | (Open + High + Low + Close) / 4 |
| \`'volume'\` | Volume |

## Bounds Checking

- \`i\` starts at 0 (first bar)
- \`ctx.series.length\` is the total number of bars
- Accessing \`ctx.series.close[i - 1]\` when \`i === 0\` returns \`undefined\`
- Accessing \`ctx.series.close[i + 1]\` is not possible — future data is never available
`,
  },
  {
    id: 'types/orders',
    title: 'Order & Position Types',
    category: 'types',
    tags: ['order', 'position', 'OrderMeta', 'Position', 'qty', 'avgPrice', 'signal', 'market', 'close', 'reduce'],
    version: '2.0',
    related: ['api/orders/market', 'api/orders/close', 'api/orders/reduce', 'api/position/current'],
    order: 5,
    description: 'Order metadata, position information, and the order API interface',
    content: `# Order & Position Types

## OrderMeta

Metadata attached to orders for tracking and analysis. All fields are optional.

\`\`\`typescript
interface OrderMeta {
  signal?: string;    // Signal name shown in results (e.g., 'buy', 'sell')
  reason?: string;    // Human-readable reason
  type?: 'full_entry' | 'full_exit' | 'partial_entry' | 'partial_exit';
  [key: string]: unknown;  // Custom fields
}
\`\`\`

\`\`\`javascript
ctx.order.market('ASSET', 1, {
  signal: 'buy',
  reason: 'ema_crossover',
  type: 'full_entry',
  confidence: 0.85,  // custom field
});
\`\`\`

## Position

Returned by \`ctx.position(symbol)\`. Reflects the current state of your position.

\`\`\`typescript
interface Position {
  qty: number;           // Quantity held (positive = long, negative = short, 0 = flat)
  avgPrice: number;      // Average entry price
  unrealizedPnL: number; // Unrealized profit/loss at current bar
  realizedPnL: number;   // Realized profit/loss from closed trades
}
\`\`\`

### Common Patterns

\`\`\`javascript
const pos = ctx.position('ASSET');

// Check if flat
if (pos.qty === 0) { /* no position */ }

// Check if long
if (pos.qty > 0) { /* holding long */ }

// Check if short
if (pos.qty < 0) { /* holding short */ }

// Check profit
if (pos.unrealizedPnL > 0) { /* in profit */ }
\`\`\`

## OrderAPI

The full order interface available via \`ctx.order\`:

\`\`\`typescript
interface OrderAPI {
  market(symbol: string, qty: number, meta?: OrderMeta): void;
  close(symbol: string, meta?: OrderMeta): void;
  reduce(symbol: string, pct: number, meta?: OrderMeta): void;
  limit(symbol: string, qty: number, price: number, meta?: OrderMeta): void;
  stop(symbol: string, qty: number, stopPrice: number, meta?: OrderMeta): void;
}
\`\`\`

| Method | Description |
|--------|-------------|
| \`market()\` | Immediate fill at next bar's open |
| \`close()\` | Close entire position |
| \`reduce()\` | Reduce position by a percentage (0–1) |
| \`limit()\` | Fill when price reaches the limit |
| \`stop()\` | Fill when price reaches the stop |

See the [Strategy API](api/orders/market) for detailed usage of each method.
`,
  },
  {
    id: 'types/helpers',
    title: 'Helper Functions (q)',
    category: 'types',
    tags: ['helpers', 'q', 'crossOver', 'crossUnder', 'isNaN', 'highest', 'lowest', 'sma', 'clamp', 'between', 'pctChange', 'sign', 'abs', 'round'],
    version: '2.0',
    related: ['api/helpers/overview', 'types/series'],
    order: 6,
    description: 'All globally available helper functions in the q namespace',
    content: `# Helper Functions (q)

The \`q\` namespace provides 12 utility functions available globally in \`onBar()\`. These are pure functions — they do not modify state.

## Reference

| Function | Signature | Description |
|----------|-----------|-------------|
| \`q.crossOver\` | \`(a: number[], b: number[]\\|number, i: number) => boolean\` | \`a\` crosses above \`b\` at bar \`i\` |
| \`q.crossUnder\` | \`(a: number[], b: number[]\\|number, i: number) => boolean\` | \`a\` crosses below \`b\` at bar \`i\` |
| \`q.highest\` | \`(series: number[], period: number, i: number) => number\` | Highest value in lookback |
| \`q.lowest\` | \`(series: number[], period: number, i: number) => number\` | Lowest value in lookback |
| \`q.sma\` | \`(series: number[], period: number, i: number) => number\` | Simple moving average over lookback |
| \`q.isNaN\` | \`(value: number) => boolean\` | Check if indicator is not yet computed |
| \`q.clamp\` | \`(value: number, min: number, max: number) => number\` | Constrain value within range |
| \`q.between\` | \`(value: number, min: number, max: number) => boolean\` | Check if value is within range |
| \`q.pctChange\` | \`(a: number, b: number) => number\` | Percentage change from \`b\` to \`a\` |
| \`q.sign\` | \`(value: number) => -1 \\| 0 \\| 1\` | Sign of a number |
| \`q.abs\` | \`(value: number) => number\` | Absolute value |
| \`q.round\` | \`(value: number, decimals: number) => number\` | Round to N decimal places |

## Examples

\`\`\`javascript
function onBar(ctx, i) {
  const close = ctx.series.close;
  const ema = ctx.ind.ema;

  // Skip bars where indicator isn't ready
  if (q.isNaN(ema[i])) return;

  // Detect crossover
  if (q.crossOver(close, ema, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy' });
  }

  // Use a constant threshold
  if (q.crossUnder(ctx.ind.rsi, 30, i)) {
    ctx.order.market('ASSET', 1, { signal: 'rsi_oversold' });
  }

  // Donchian-style breakout using helpers
  const highest20 = q.highest(ctx.series.high, 20, i);
  if (close[i] > highest20) {
    ctx.order.market('ASSET', 1, { signal: 'breakout' });
  }

  // Inline SMA for volume filter
  const avgVol = q.sma(ctx.series.volume, 20, i);
  if (ctx.series.volume[i] < avgVol * 0.5) return; // Low volume, skip

  // Position sizing with clamp
  const rawSize = q.round(10000 / close[i], 4);
  const size = q.clamp(rawSize, 0.01, 5);
  
  // RSI neutral zone check
  if (q.between(ctx.ind.rsi[i], 40, 60)) {
    // Neutral — no strong signal
  }
}
\`\`\`

## Notes

- \`q.crossOver\` and \`q.crossUnder\` accept either an array or a constant as the second argument
- All lookback functions (\`highest\`, \`lowest\`, \`sma\`) return \`NaN\` if the lookback window extends before bar 0
- \`q.pctChange(a, b)\` returns \`(a - b) / b\` as a decimal (0.05 = 5%)
`,
  },
];
